import { useEffect, useState } from "react";

import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { fetchMissingServicesFilterProviders } from "@/billing/services/missing-services-filter-providers";
import { MissingServicesFilterProvider } from "@/billing/types/missing-services";

/**
 * Loads the practice's Practitioners eligible for the Filter Services drawer's Provider section
 * (the roles that can sign and lock an encounter — see `fetchMissingServicesFilterProviders`), once
 * per practice per time the drawer opens. Mirrors `useCalendarFilterData`'s lazy-on-open fetch.
 */
export function useMissingServicesFilterProviders(
  practiceId: string | undefined,
  opened: boolean,
): { providers: MissingServicesFilterProvider[]; isLoading: boolean } {
  const medplum = useMedplum();
  const [providers, setProviders] = useState<MissingServicesFilterProvider[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // The practiceId the current `providers` were fetched for — undefined means not yet fetched.
  // `practiceId` is a route param, so it can change while this hook stays mounted (no remount);
  // comparing against it (rather than a plain boolean) keeps re-fetching when it does.
  const [fetchedForPracticeId, setFetchedForPracticeId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (fetchedForPracticeId !== undefined && fetchedForPracticeId !== practiceId) {
      // The practice changed since the last fetch — those providers no longer apply.
      setProviders([]);
      setFetchedForPracticeId(undefined);
      return;
    }

    if (!opened || !practiceId || fetchedForPracticeId === practiceId) return;

    let cancelled = false;
    setIsLoading(true);

    fetchMissingServicesFilterProviders(medplum, practiceId)
      .then((result) => {
        if (cancelled) return;
        // Set alongside isLoading, not in a trailing .finally(): setting fetchedForPracticeId here
        // triggers this same effect's cleanup (it's a dependency) before a separate .finally()
        // microtask would run, which would otherwise skip setIsLoading(false) by seeing cancelled=true.
        setProviders(result);
        setFetchedForPracticeId(practiceId);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        Sentry.captureException(error);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [opened, fetchedForPracticeId, medplum, practiceId]);

  return { providers, isLoading };
}
